import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  useColorModeValue,
  Alert,
  AlertIcon,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { updateConsentInSharePoint } from '../../../services/sharepointService';
import { useGraphToken } from '../../../auth/MsalProvider';

const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const platform = searchParams.get('platform') || 'invyteout'; // Default to invyteout if not specified
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { getAccessToken } = useGraphToken();
  
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  
  useEffect(() => {
    // If email is provided in URL, show confirmation UI
    if (!email) {
      setStatus('error');
      setErrorMessage('No email address provided. Please check the link and try again.');
    }
  }, [email]);
  
  const handleUnsubscribe = async () => {
    if (!email) return;
    
    setStatus('loading');
    // Removed console.log about unsubscribe attempt
    
    try {
      // Get access token first
      const accessToken = await getAccessToken();
      
      // Log which platform we're unsubscribing from
      // Removed console.log about platform unsubscribe
      
      // Call the SharePoint service to update consent
      const success = await updateConsentInSharePoint(email, 'email', false, accessToken, platform);
      
      if (success) {
        // Removed console.log about successful unsubscribe
        // Add a small delay before updating UI state to ensure all browser operations complete
        setTimeout(() => {
          setStatus('success');
        }, 300);
      } else {
        // Removed console.error about unsubscribe failure
        setStatus('error');
        setErrorMessage(
          'Could not unsubscribe. The email address may not be found in our system or may be formatted differently than in our records.'
        );
      }
    } catch (error) {
      // Removed console.error about unsubscribe error
      setStatus('error');
      setErrorMessage(
        'An unexpected error occurred. Please try again later or contact support with the email address you used to sign up.'
      );
    }
  };
  
  return (
    <Box bg={bgColor} minH="100vh" py={10}>
      <Container maxW="container.md">
        <VStack 
          spacing={8} 
          bg={cardBgColor} 
          p={8} 
          borderRadius="lg" 
          boxShadow="md"
          align="center"
        >
          <Heading as="h1" size="xl" color={textColor}>
            Unsubscribe from Updates
          </Heading>
          
          {status === 'error' && email && (
            <Text fontSize="md" color="red.500" fontWeight="bold" mt={2}>
              Attempted to unsubscribe: {email}
            </Text>
          )}
          
          {status === 'idle' && email && (
            <>
              <Text fontSize="lg" textAlign="center">
                Are you sure you want to unsubscribe {email} from our waitlist updates?
              </Text>
              <Button 
                colorScheme="red" 
                size="lg" 
                onClick={handleUnsubscribe}
              >
                Confirm Unsubscribe
              </Button>
              <Text fontSize="sm" color="gray.500">
                You will no longer receive updates about our product.
              </Text>
            </>
          )}
          
          {status === 'loading' && (
            <Center p={8}>
              <VStack spacing={4}>
                <Spinner size="xl" />
                <Text>Processing your request...</Text>
              </VStack>
            </Center>
          )}
          
          {status === 'success' && (
            <>
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                You have been successfully unsubscribed.
              </Alert>
              <Text mt={4}>
                You will no longer receive updates from us. Your consent preferences have been updated in our SharePoint database.
              </Text>
              <Text mt={2}>
                If you change your mind, you can always sign up again on our website.
              </Text>
            </>
          )}
          
          {status === 'error' && (
            <>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {errorMessage || 'An error occurred while processing your request.'}
              </Alert>
              <Text mt={4}>
                If you continue to have issues, please contact our support team.
              </Text>
            </>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default UnsubscribePage;
